/**
 * Static COP-32 architecture copy for executive / leadership mocks (aligned with national ILO / C&N / RM / M&C / DS streams).
 * Pillar names and deliverable lines mirror the PMO task architecture — values remain mock.
 */

/** ILO — 14 pillars; each `lines` are representative deliverables (subset of full task architecture). */
export const ILO_PILLAR_ARCHITECTURE = [
    {
        slug: 'venue',
        name: 'Venue',
        description:
            'Venue strategic alignment with the national COP roadmap, zoning, capacity, simulations, and completion tracking.',
        lines: [
            'Develop a comprehensive venue strategic document aligned with the national COP roadmap',
            'Define total usable venue area (sqm) and allocate by function (plenary, offices, circulation)',
            'Finalize blue zone and green zone spatial zoning (mapped and approved)',
            'Confirm number and capacity of plenary halls and meeting rooms',
            'Complete site master plan with circulation and access routes',
        ],
    },
    {
        slug: 'accommodation',
        name: 'Accommodation',
        description:
            'Accommodation strategy, room inventory, delegation allocation, hotel agreements, and readiness audit.',
        lines: [
            'Develop an accommodation strategy aligned with the national roadmap',
            'Quantify total room inventory (by category and star rating)',
            'Match room capacity against projected delegate numbers (gap analysis)',
        ],
    },
    {
        slug: 'security',
        name: 'Security',
        description:
            'Integrated security strategy, personnel, checkpoints, perimeter, surveillance, and threat simulations.',
        lines: [
            'Develop an integrated security strategy aligned with the national roadmap',
            'Define total number of security personnel required and deployed',
            'Establish number of screening checkpoints (fixed and mobile)',
        ],
    },
    {
        slug: 'protocol',
        name: 'Protocol',
        description:
            'Protocol and diplomatic operations, VIP tiers, receptions, bilateral schedules, and SOPs for ceremonies.',
        lines: [
            'Develop protocol and diplomatic operations strategy aligned with national roadmap',
            'Define number of VIPs and delegation tiers',
            'Map protocol service requirements per category',
        ],
    },
    {
        slug: 'transport',
        name: 'Transport',
        description: 'Transport and mobility strategy, fleet, routes, GPS, capacity, and on-time performance.',
        lines: [
            'Develop transport and mobility strategy aligned with the national roadmap',
            'Quantify total fleet required (buses, shuttles, VIP vehicles)',
            'Map transport routes and average travel times (minutes)',
        ],
    },
    {
        slug: 'aviation',
        name: 'Aviation',
        description: 'Aviation and airspace strategy, flight volumes, airport capacity, VIP slots, and readiness simulation.',
        lines: [
            'Develop aviation and airspace strategy aligned with the national roadmap',
            'Quantify total expected arrivals/departures (flights per day)',
            'Define airport processing capacity (passengers/hour)',
        ],
    },
    {
        slug: 'immigration',
        name: 'Immigration',
        description: 'Immigration and border processing, visas, fast-track lanes, customs, and accreditation integration.',
        lines: [
            'Develop immigration and border processing strategy aligned with the national roadmap',
            'Quantify visa applications expected and processed',
            'Define processing capacity (visas/day and passengers/hour)',
        ],
    },
    {
        slug: 'ict_telecom',
        name: 'ICT and Telecom',
        description: 'ICT and telecom strategy, bandwidth, Wi-Fi, redundancy, cybersecurity, and stress testing.',
        lines: [
            'Develop ICT and telecom strategy aligned with the national roadmap',
            'Define total bandwidth requirements (Gbps)',
            'Install number of Wi-Fi access points across venue',
        ],
    },
    {
        slug: 'health_medical',
        name: 'Health and Medical',
        description: 'Medical and emergency response, stations, ambulances, referrals, and mass-casualty simulation.',
        lines: [
            'Develop medical and emergency response strategy aligned with national roadmap',
            'Quantify number of medical stations and personnel',
            'Define ambulance coverage (units and response time in minutes)',
        ],
    },
    {
        slug: 'power_utilities',
        name: 'Power and Other Utilities',
        description: 'Utilities strategy: power demand, backup, water, sanitation, uptime, and waste tracking.',
        lines: [
            'Develop utilities strategy aligned with the national roadmap',
            'Quantify total power demand (MW) and supply capacity',
            'Install backup power systems (capacity in MWh)',
        ],
    },
    {
        slug: 'catering',
        name: 'Catering',
        description: 'Catering strategy, meals, providers, distribution points, food safety, and service timing.',
        lines: [
            'Develop catering strategy aligned with national roadmap',
            'Quantify meals required per day (by category)',
            'Define number of catering providers contracted',
        ],
    },
    {
        slug: 'culture_engagement',
        name: 'Culture and Public Engagement',
        description: 'Culture and public engagement: events, spaces, participation, branding, and outreach.',
        lines: [
            'Develop culture and public engagement strategy aligned with national roadmap',
            'Define number of cultural events and exhibitions',
            'Allocate engagement spaces (sqm and locations)',
        ],
    },
    {
        slug: 'cities',
        name: 'Cities',
        description: 'City readiness: roles, infrastructure, services, inspections, mobility, and citizen engagement.',
        lines: [
            'Develop city readiness strategy aligned with national roadmap',
            'Define number of cities involved and their roles',
            'Assess road and infrastructure readiness (% completed)',
        ],
    },
    {
        slug: 'state_dinner',
        name: 'State Dinner',
        description: 'State dinner strategy: guests, layout, menu, protocol seating, rehearsal, and service timing.',
        lines: [
            'Develop state dinner strategy aligned with the national roadmap',
            'Define number of guests and seating capacity',
            'Finalize venue and layout plan (sqm and seating map)',
        ],
    },
];

/** Media & Communications — subunits (5 deliverables each in full architecture; mock uses first 3 per subunit). */
export const MC_ARCHITECTURE = [
    {
        slug: 'mc_content',
        name: 'Content Subunit',
        description: 'National COP content strategy, production targets, narrative packages, delivery rate, and reach.',
        taskLines: [
            'Develop a national COP content strategy aligned with the national roadmap',
            'Define total content production targets (number of outputs by type: video, articles, briefs)',
            'Produce core narrative content packages (number produced)',
        ],
    },
    {
        slug: 'mc_strategy',
        name: 'Strategy Subunit',
        description: 'Integrated communications strategy, messaging pillars, campaign calendar, and performance reports.',
        taskLines: [
            'Develop an integrated communications strategy aligned with the national roadmap',
            'Define key messaging pillars and priority themes (number defined)',
            'Establish communication timelines and campaign calendar (milestones achieved %)',
        ],
    },
    {
        slug: 'mc_partnership',
        name: 'Partnership Subunit',
        description: 'Media and communications partnerships, co-production, partner outputs, and reach share.',
        taskLines: [
            'Develop media and communications partnership strategy aligned with the national roadmap',
            'Identify and onboard media and content partners (number secured)',
            'Establish co-production and distribution agreements (number signed)',
        ],
    },
    {
        slug: 'mc_media',
        name: 'Media Subunit',
        description: 'Media engagement, accreditation, press briefings, coverage volume, and sentiment.',
        taskLines: [
            'Develop media engagement strategy aligned with the national roadmap',
            'Accredit and onboard media organizations (number accredited)',
            'Conduct press briefings and media engagements (number held)',
        ],
    },
    {
        slug: 'mc_digital',
        name: 'Digital Subunit',
        description: 'Digital communications, platform targets, publishing cadence, engagement metrics, and growth.',
        taskLines: [
            'Develop digital communications strategy aligned with the national roadmap',
            'Define digital platform targets (followers, subscribers, traffic numbers)',
            'Produce and publish digital content (number of posts, videos, campaigns)',
        ],
    },
    {
        slug: 'mc_me',
        name: 'M&E Subunit',
        description: 'Communications M&E framework, deliverables for subunits, dashboard coverage, reviews, and reports.',
        taskLines: [
            'Develop communications monitoring and evaluation framework aligned with the national roadmap',
            'Define Milestones for all subunits (number of Milestones established)',
            'Track performance across channels (dashboard coverage %)',
        ],
    },
    {
        slug: 'mc_green_zone',
        name: 'Green Zone Activation & Programming Unit',
        description: 'Green Zone programming strategy, events, schedule slots, participation, and satisfaction.',
        taskLines: [
            'Develop Green Zone programming strategy aligned with the national roadmap',
            'Define number of events and activation programs',
            'Allocate programming spaces and schedule (number of slots filled)',
        ],
    },
    {
        slug: 'mc_media_center',
        name: 'COP32 Media Center',
        description: 'Media Center operations: capacity, infrastructure readiness, daily usage, and service performance.',
        taskLines: [
            'Develop Media Center operational strategy aligned with the national roadmap',
            'Define capacity (number of journalists accommodated)',
            'Establish infrastructure readiness (workstations, connectivity – % operational)',
        ],
    },
    {
        slug: 'mc_crisis',
        name: 'Crisis Communications',
        description: 'Crisis communications: scenarios, response protocols, simulations, and incident performance.',
        taskLines: [
            'Develop crisis communication strategy aligned with the national roadmap',
            'Define crisis scenarios and response protocols (number of scenarios mapped)',
            'Establish response time targets (minutes/hours per incident)',
        ],
    },
];

/** Resource Mobilization — 9 pillars. */
export const RM_ARCHITECTURE = [
    {
        slug: 'rm_bilateral',
        name: 'Bilateral Government Partners',
        description: 'Bilateral engagement strategy, partner mapping, meetings, commitments, and partnership briefs.',
        taskLines: [
            'Develop bilateral engagement strategy aligned with the national roadmap',
            'Identify and prioritize target countries (number of partners mapped)',
            'Conduct bilateral engagements (number of meetings held)',
        ],
    },
    {
        slug: 'rm_multilateral',
        name: 'Multilateral and Regional Organizations',
        description: 'Multilateral strategy, institutions mapped, engagements, support secured, and MoUs.',
        taskLines: [
            'Develop multilateral engagement strategy aligned with the national roadmap',
            'Identify priority organizations (number of institutions mapped)',
            'Conduct formal engagements (meetings, missions, dialogues – number held)',
        ],
    },
    {
        slug: 'rm_mdb',
        name: 'MDBs and DFIs',
        description: 'MDB/DFI engagement, financing windows, proposals, approvals, and investment agreements.',
        taskLines: [
            'Develop MDB/DFI engagement strategy aligned with the national roadmap',
            'Identify financing windows and instruments (number identified)',
            'Prepare and submit funding proposals (number submitted)',
        ],
    },
    {
        slug: 'rm_private',
        name: 'Private Sector',
        description: 'Private sector engagement, target mapping, outreach, sponsorships, and partnership agreements.',
        taskLines: [
            'Develop private sector engagement strategy aligned with the national roadmap',
            'Identify target companies and industries (number mapped)',
            'Conduct investor and corporate outreach (number of engagements)',
        ],
    },
    {
        slug: 'rm_philanthropy',
        name: 'Philanthropic Foundations and Re-granters',
        description: 'Philanthropy engagement, foundations mapped, grant proposals, grants secured, and agreements.',
        taskLines: [
            'Develop philanthropy engagement strategy aligned with the national roadmap',
            'Identify priority foundations and re-granters (number mapped)',
            'Submit grant proposals (number submitted)',
        ],
    },
    {
        slug: 'rm_cso',
        name: 'Civil Society Organizations and Non-State Actors',
        description: 'CSO engagement, mapping, consultations, partnerships, and collaboration agreements.',
        taskLines: [
            'Develop CSO engagement strategy aligned with the national roadmap',
            'Identify and map CSOs and networks (number mapped)',
            'Conduct engagement sessions and consultations (number held)',
        ],
    },
    {
        slug: 'rm_technical',
        name: 'Technical and Knowledge Partners',
        description: 'Technical partnerships with think tanks, universities, collaboration frameworks, and deliverables.',
        taskLines: [
            'Develop technical partnership strategy aligned with the national roadmap',
            'Identify think tanks, universities, and consultancies (number mapped)',
            'Establish technical collaboration frameworks (number agreed)',
        ],
    },
    {
        slug: 'rm_advocacy',
        name: 'Strategic Advocacy Partners',
        description: 'Advocacy and influence strategy, platforms mapped, campaigns, outcomes, and coalition statements.',
        taskLines: [
            'Develop advocacy and influence strategy aligned with the national roadmap',
            'Identify global and regional advocacy platforms (number mapped)',
            'Conduct advocacy engagements and campaigns (number executed)',
        ],
    },
    {
        slug: 'rm_soe',
        name: 'State-Owned Enterprises and Sovereign Funds',
        description: 'SOE and sovereign fund engagement, mapping, investment engagements, capital commitments, and agreements.',
        taskLines: [
            'Develop SOE and sovereign fund engagement strategy aligned with the national roadmap',
            'Identify target SOEs and sovereign funds (number mapped)',
            'Conduct high-level investment engagements (number held)',
        ],
    },
];

/** Digital Systems — 6 pillars. */
export const DS_ARCHITECTURE = [
    {
        slug: 'ds_readiness',
        name: 'Readiness Tracking System',
        description: 'Readiness tracking strategy, pillars/indicators integrated, modules, users, and data completion.',
        taskLines: [
            'Develop a readiness tracking system strategy aligned with the national COP roadmap',
            'Define total number of pillars, indicators, and questions integrated into the system',
            'Build and deploy system modules (percentage of modules completed)',
        ],
    },
    {
        slug: 'ds_ops',
        name: 'Operations Management System',
        description: 'Operations management system: tasks, workflows, completion rates, and performance metrics.',
        taskLines: [
            'Develop operations management system strategy aligned with the national roadmap',
            'Define total number of tasks, workflows, and modules configured',
            'Track number of tasks created, assigned, and completed (%)',
        ],
    },
    {
        slug: 'ds_occ',
        name: 'Operations Control Center and Command System',
        description: 'Operations control center: integrated systems, dashboard coverage, decisions, and response time.',
        taskLines: [
            'Develop operations control center strategy aligned with the national roadmap',
            'Define number of integrated systems feeding into the control center',
            'Establish real-time dashboard coverage (% of pillars integrated)',
        ],
    },
    {
        slug: 'ds_incident',
        name: 'Incident and Risk Management System',
        description: 'Incident and risk management: categories, incidents logged, response time, and resolution rate.',
        taskLines: [
            'Develop incident and risk management strategy aligned with the national roadmap',
            'Define number of risk categories and scenarios mapped',
            'Track number of incidents reported and logged',
        ],
    },
    {
        slug: 'ds_interagency',
        name: 'Interagency Coordination System',
        description: 'Interagency coordination: agencies integrated, joint actions, turnaround time, and effectiveness.',
        taskLines: [
            'Develop interagency coordination system strategy aligned with the national roadmap',
            'Define number of agencies integrated into the system',
            'Track number of coordination tasks and joint actions executed',
        ],
    },
    {
        slug: 'ds_legacy',
        name: 'Post-COP Data and Legacy System',
        description: 'Post-COP legacy: datasets archived, completeness, users, and utilization metrics.',
        taskLines: [
            'Develop post-COP data and legacy strategy aligned with the national roadmap',
            'Define total datasets, documents, and assets archived',
            'Track data completeness and integrity (% verified and validated)',
        ],
    },
];

/** Content & Negotiations — 14 pillars. */
export const CN_ARCHITECTURE = [
    {
        slug: 'cn_science_ocean',
        name: 'Science and Ocean',
        description: 'Science and Ocean negotiation strategy, position papers, interventions, and briefing notes.',
        taskLines: [
            'Develop a Science and Ocean negotiation strategy aligned with the national roadmap',
            'Produce national scientific position papers (number of submissions)',
            'Define priority negotiation issues (ranked list with justification)',
        ],
    },
    {
        slug: 'cn_transparency',
        name: 'Transparency',
        description: 'Transparency negotiation strategy, reporting gaps, framework submission, and MRV briefs.',
        taskLines: [
            'Develop transparency negotiation strategy aligned with the national roadmap',
            'Define national reporting gaps (number of gaps identified)',
            'Prepare transparency framework submission (documents submitted)',
        ],
    },
    {
        slug: 'cn_mitigation',
        name: 'Mitigation',
        description: 'Mitigation strategy, NDC targets, sectoral proposals, interventions, and scenario models.',
        taskLines: [
            'Develop mitigation negotiation strategy aligned with the national roadmap',
            'Quantify national mitigation targets (NDC updates, % reductions)',
            'Prepare sectoral mitigation proposals (number of sectors covered)',
        ],
    },
    {
        slug: 'cn_response',
        name: 'Response Measures',
        description: 'Response measures strategy, sectoral impacts, position papers, engagement, and impact reports.',
        taskLines: [
            'Develop response measures strategy aligned with the national roadmap',
            'Identify economic and social impacts (number of sectors assessed)',
            'Prepare national position papers (documents submitted)',
        ],
    },
    {
        slug: 'cn_just_transition',
        name: 'Just Transition',
        description: 'Just transition strategy, affected sectors, pathways, negotiation positions, and policy briefs.',
        taskLines: [
            'Develop just transition strategy aligned with the national roadmap',
            'Identify affected sectors and workforce (number mapped)',
            'Define transition pathways (number of pathways developed)',
        ],
    },
    {
        slug: 'cn_art6',
        name: 'Article 6 and CDM',
        description: 'Article 6/CDM strategy, eligible projects, carbon credit potential, cooperation agreements, and submissions.',
        taskLines: [
            'Develop Article 6/CDM strategy aligned with the national roadmap',
            'Identify eligible carbon market projects (number identified)',
            'Define carbon credit potential (estimated volume in tons)',
        ],
    },
    {
        slug: 'cn_finance',
        name: 'Climate Finance',
        description: 'Climate finance strategy, financing needs, funding sources, pledges, and investment cases.',
        taskLines: [
            'Develop climate finance strategy aligned with the national roadmap',
            'Quantify national financing needs (USD value)',
            'Identify funding sources and partners (number identified)',
        ],
    },
    {
        slug: 'cn_tech_transfer',
        name: 'Technology Development and Transfer',
        description: 'Technology transfer strategy, priority technologies, gap analysis, partnerships, and proposals.',
        taskLines: [
            'Develop technology transfer strategy aligned with the national roadmap',
            'Identify priority technologies (number of technologies listed)',
            'Map technology gaps and needs (gap analysis completed)',
        ],
    },
    {
        slug: 'cn_capacity',
        name: 'Capacity Building',
        description: 'Capacity-building strategy, institutional gaps, training programs, personnel trained, and strengthening plans.',
        taskLines: [
            'Develop capacity-building strategy aligned with the national roadmap',
            'Identify capacity gaps across institutions (number identified)',
            'Design training programs (number of programs developed)',
        ],
    },
    {
        slug: 'cn_compliance',
        name: 'Compliance and Implementation',
        description: 'Compliance strategy, UNFCCC obligations, timelines, performance indicators, and monitoring reports.',
        taskLines: [
            'Develop compliance strategy aligned with the national roadmap',
            'Map national obligations under UNFCCC (number of obligations tracked)',
            'Define implementation timelines (milestones achieved %)',
        ],
    },
    {
        slug: 'cn_gender',
        name: 'Gender',
        description: 'Gender-responsive climate strategy, gender gaps, negotiation submissions, participation, and assessments.',
        taskLines: [
            'Develop gender-responsive climate strategy aligned with the national roadmap',
            'Identify gender gaps in climate policy (number identified)',
            'Integrate gender into negotiation positions (number of submissions)',
        ],
    },
    {
        slug: 'cn_ace',
        name: 'ACE and International Organizations',
        description: 'ACE strategy, international partnerships, awareness targets, engagement activities, and implementation reports.',
        taskLines: [
            'Develop ACE (Action for Climate Empowerment) strategy aligned with the national roadmap',
            'Identify partnerships with international organizations (number established)',
            'Define public awareness and education targets (audience reach)',
        ],
    },
    {
        slug: 'cn_hle',
        name: 'High-Level Engagement',
        description: 'High-level engagement strategy, leaders mapped, bilateral/multilateral meetings, interventions, and briefing packages.',
        taskLines: [
            'Develop high-level engagement strategy aligned with the national roadmap',
            'Identify key leaders and strategic partners (number mapped)',
            'Schedule bilateral and multilateral meetings (number confirmed)',
        ],
    },
    {
        slug: 'cn_other_tracks',
        name: 'Other Negotiation Tracks',
        description: 'Strategy for emerging tracks, additional areas, focal points, engagement coverage, and consolidated reports.',
        taskLines: [
            'Develop strategy for emerging/other negotiation tracks aligned with the national roadmap',
            'Identify additional negotiation areas (number identified)',
            'Assign focal points for each track (number assigned)',
        ],
    },
];
